<template>
	<div class="cell-dropdown" :class="{ readonly }" @click.stop>
		<template v-if="readonly">
			<img
				v-if="selectedOption?.iconUrl"
				:src="selectedOption.iconUrl"
				:alt="selectedOption.label"
				:title="selectedOption.label"
				class="icon"
			/>
			<span v-else-if="selectedOption" class="label">{{ selectedOption.label }}</span>
			<span v-else-if="fallbackLabel" class="label fallback">{{ fallbackLabel }}</span>
		</template>
		<Popover.Root v-else v-model:open="isOpen" :positioning="{ placement: 'bottom-end', gutter: 4, strategy: 'fixed' }">
			<Popover.Trigger as-child>
				<button type="button" class="trigger" :class="{ open: isOpen }">
					<img v-if="selectedOption?.iconUrl" :src="selectedOption.iconUrl" :alt="selectedOption.label" class="icon" />
					<span v-else-if="selectedOption" class="label">{{ selectedOption.label }}</span>
					<span v-else-if="fallbackLabel" class="label">{{ fallbackLabel }}</span>
					<span v-else class="placeholder">—</span>
					<AppIcon :name="IconName.ChevronDownSmall" class="chevron" />
				</button>
			</Popover.Trigger>

			<Popover.Positioner>
				<Popover.Content class="panel">
					<div v-if="isSearchable" class="search-container">
						<input
							v-model="searchQuery"
							type="text"
							class="search-input"
							:placeholder="searchPlaceholder"
							@click.stop
						/>
					</div>
					<button
						v-for="opt in filteredOptions"
						:key="opt.value"
						type="button"
						class="option"
						:class="{ selected: opt.value === value }"
						@click="select(opt.value)"
					>
						<span class="label">{{ opt.label }}</span>
						<img v-if="opt.iconUrl" :src="opt.iconUrl" :alt="opt.label" class="icon" />
						<AppIcon v-if="opt.value === value" :name="IconName.CheckSmall" class="check" />
					</button>
					<template v-if="clearLabel !== undefined">
						<button type="button" class="option" :class="{ selected: !value }" @click="select(null)">
							<span class="label">{{ clearLabel }}</span>
							<AppIcon v-if="!value" :name="IconName.CheckSmall" class="check" />
						</button>
					</template>
				</Popover.Content>
			</Popover.Positioner>
		</Popover.Root>
	</div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { Popover } from "@ark-ui/vue"
import { useI18n } from "vue-i18n"

export type CellDropdownOption = {
	value: string
	label: string
	iconUrl?: string
}

const props = withDefaults(
	defineProps<{
		value: string | null | undefined
		options: CellDropdownOption[]
		readonly: boolean
		clearLabel?: string
		fallbackLabel?: string
		searchThreshold?: number
	}>(),
	{
		searchThreshold: 6,
	}
)

const emit = defineEmits<{
	(e: "change", value: string | null): void
}>()

const { t } = useI18n()

const isOpen = ref(false)
const searchQuery = ref("")

const selectedOption = computed(() => props.options.find(o => o.value === props.value) ?? null)

const isSearchable = computed(() => props.options.length > props.searchThreshold)
const searchPlaceholder = computed(() => t("filters.filters-search"))

const filteredOptions = computed(() => {
	if (!isSearchable.value || !searchQuery.value.trim()) return props.options
	const q = searchQuery.value.toLowerCase()
	return props.options.filter(o => o.label.toLowerCase().includes(q))
})

watch(isOpen, open => {
	if (!open) searchQuery.value = ""
})

function select(value: string | null): void {
	isOpen.value = false
	emit("change", value)
}
</script>

<style scoped>
.cell-dropdown {
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;

	.icon {
		width: var(--size-icon-lg);
		height: var(--size-icon-lg);
		flex-shrink: 0;
		display: block;
	}

	.label {
		font-size: var(--font-size-small);
		white-space: nowrap;
	}

	.trigger {
		display: inline-flex;
		align-items: center;
		gap: var(--space-3);
		background: none;
		border: none;
		padding: var(--space-2) var(--space-4);
		border-radius: var(--border-radius-sm);
		cursor: pointer;
		color: inherit;

		&:hover {
			background: var(--neutral-200);
		}

		&:focus-visible {
			outline: var(--spacing-xxs) solid var(--primary-green);
			outline-offset: var(--spacing-xxs);
		}

		.chevron {
			width: var(--space-8);
			height: 5px;
			flex-shrink: 0;
			opacity: 0.45;
			transition: transform 0.15s ease;
		}

		.placeholder {
			font-size: var(--font-size-small);
			color: var(--neutral-600);
		}

		&.open .chevron {
			transform: rotate(180deg);
			opacity: 0.7;
		}
	}
}

.panel {
	z-index: 200;
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-base);
	box-shadow: var(--shadow-sm);
	min-width: var(--space-160);
	max-width: 220px;
	padding: var(--spacing-xxs);
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xxs);

	.search-container {
		padding-bottom: var(--spacing-xxs);
		border-bottom: var(--border-width-thin) solid var(--neutral-200);
	}

	.search-input {
		width: 100%;
		padding: var(--spacing-xs) var(--space-6);
		border: none;
		border-radius: var(--border-radius-sm);
		font-size: var(--font-size-small);
		font-family: inherit;
		outline: none;
	}

	.option {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		padding: var(--spacing-xs) var(--space-6);
		min-height: var(--space-30);
		box-sizing: border-box;
		background: none;
		border: var(--spacing-xxs) solid transparent;
		border-radius: var(--border-radius-base);
		cursor: pointer;
		text-align: left;
		font-size: var(--font-size-small);
		font-family: inherit;
		color: var(--neutral-900);
		width: 100%;
		transition: background-color 0.15s ease;

		.label {
			white-space: normal;
			line-height: 1.3;
		}

		.icon {
			width: var(--size-icon-md);
			height: var(--size-icon-md);
			margin-left: var(--spacing-xs);
		}

		&:hover {
			background: var(--neutral-100);
		}

		&.selected {
			background: var(--neutral-50);
			color: var(--neutral-900);
		}

		&:focus-visible {
			outline: 2px solid var(--primary-green);
			outline-offset: -2px;
		}

		.check {
			width: var(--space-12);
			height: 9px;
			margin-left: auto;
			flex-shrink: 0;
			color: var(--neutral-700);
		}
	}
}
</style>
